/* eslint-disable @typescript-eslint/member-ordering */
import { Component, Input, output, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { Editor } from '@ckeditor/ckeditor5-core';
import { faCode } from '@fortawesome/pro-regular-svg-icons';

import { ContentPipe } from '../../models/content-pipe.model';
import { TextEditorSettings } from '../../models/text-editor-settings.model';
import { QuestionPipePickerComponent } from '../question-pipe-picker/question-pipe-picker.component';

@Component({
  selector: 'empathy-text-editor',
  templateUrl: 'text-editor.component.html',
  styleUrls: ['text-editor.component.scss']
})
export class TextEditorComponent implements OnInit {
  @Input() autofocus: boolean;
  @Input() allowLineBreaks: boolean;
  @Input({ required: true }) set control(ctrl: AbstractControl) {
    this.editorCtrl = ctrl as FormControl;
  }
  @Input() hasMedia: boolean;
  @Input({ required: true }) id: string;
  @Input() settings: TextEditorSettings = new TextEditorSettings();
  @Input() stopDefaultTab: boolean;
  @Input() splitOnPaste: boolean;

  tab = output();
  paste = output<string[]>();

  onChange: (value: string) => void;
  onTouched: () => void;

  editor: any = DecoupledEditor;
  editorCtrl: FormControl;
  editorInstance: any;
  faCode = faCode;
  focused: boolean;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    DecoupledEditor.defaultConfig = {
      toolbar: {
        items: ['|', 'heading', 'bold', 'italic', 'underline', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo']
      },
      image: {
        resizeUnit: 'px',
        toolbar: []
      },
      table: {
        contentToolbar: []
      },
      list: { properties: { styles: true, reversed: true, startIndex: true } },
      language: 'en'
    };
  }

  onReady(editor: Editor): void {
    this.editorInstance = editor;

    const toolbarContainer = document.querySelector(`.text-editor__toolbar_${this.id}`);
    toolbarContainer.appendChild(editor.ui.view.toolbar.element);

    editor.ui.focusTracker.on('change:isFocused', (_evt, _name, value: boolean) => {
      this.focused = value;
    });

    editor.keystrokes.set('tab', (data: KeyboardEvent, stop: Function) => {
      this.tab.emit();
      if (this.stopDefaultTab) {
        stop();
      }
    });

    editor.keystrokes.set('enter', (data: KeyboardEvent, stop: Function) => {
      this.tab.emit();
      if (!this.allowLineBreaks) {
        stop();
      }
    });

    if (this.splitOnPaste) {
      editor.editing.view.document.on('clipboardInput', (evt, data) => this.processPastedText(editor, data));
    }

    if (this.autofocus) {
      editor.editing.view.focus();
    }
  }

  onSelectPipe(): void {
    const dialogRef = this.dialog.open(QuestionPipePickerComponent);

    dialogRef.afterClosed().subscribe((pipe: ContentPipe) => {
      if (!pipe) {
        return;
      }
      let currentValue = this.editorCtrl.value;

      const virtualWrapper = document.createElement('div');
      virtualWrapper.innerHTML = currentValue;
      const targetElement = virtualWrapper.firstElementChild;

      if (targetElement) {
        targetElement.innerHTML += this.formatCurrentValue(pipe);
        currentValue = virtualWrapper.innerHTML;
      } else {
        currentValue += this.formatCurrentValue(pipe);
      }

      this.editorCtrl.setValue(currentValue);
    });
  }

  private processPastedText(editor: any, data: any): void {
    const dataTransfer = data.dataTransfer;
    const pastedText = dataTransfer.getData('text/plain');
    const lines: string[] = pastedText.split(/\r?\n/);

    let mappedLines = lines.map((line: string) => line.replace(/.*\t/g, '').trim());
    mappedLines = mappedLines.filter((line: string) => line.length > 0);
    if (mappedLines.length === 0) {
      return;
    }

    setTimeout(() => {
      this.editorCtrl.setValue(mappedLines[0]);
      editor.data.set(mappedLines[0]);
      mappedLines.splice(0, 1);
      this.paste.emit(mappedLines);

      setTimeout(() => {
        editor.editing.view.focus();
        editor.model.change((writer) => {
          writer.setSelection(writer.createPositionAt(editor.model.document.getRoot(), 'end'));
        });
      });
    });
  }

  private formatCurrentValue(pipe: ContentPipe): string {
    if (pipe.qId) {
      return ` [${pipe.qId} | ${pipe.cardId ? `${pipe.cardId}:` : ''} ${pipe.rankId ? `${pipe.rankId}:` : ''}${pipe.format}]`;
    }

    if (pipe.loopOption) {
      return ' [loopOption]';
    }
  }
}
